/* ============================================================================
   Stacked hero + banner modules — one shared size, photo band and text band
   equally tall.

   The homepage carries five of these modules: the hero, three image-with-text
   banners (OUR STORY / OUR CRAFT / NEWEST ITEMS) and the shop-by-stone split
   panel. The hero only takes part while it is in its two-column "split" layout
   — switched to "full" it is a single full-bleed image and is skipped (SKIP
   below).

   ABOVE 750px this is pure CSS and already uniform: each module is a two-column
   grid with align-items: stretch, so the photo and the text share the row
   height and every module comes out the same size
   (see "Equal-height media columns" in base.css).

   BELOW 750px all five stack — photo on top, text underneath — so there is no
   shared row and each module drifts to its own height: the photo is pinned to
   1100 / 619 (242px on a 430px phone) while the text panel grows with its copy
   (244px to 371px across the three banners).

   Fix, in two steps:
     1. measure every module at its natural height,
     2. take the tallest — the module that needs the most room — and give that
        single height to all five, photo band and text band alike.

   The reference size is the product page's own image-with-text block, declared
   in base.css as --stacked-band (231px band / 462px module on a 430px phone).
   base.css also tightens the phone type scale so the longest banner copy fits
   inside that band, which stops one long paragraph from stretching every module
   on the page.

   Photos keep object-fit: cover, so they crop rather than letterbox. Every
   inline style is written fresh from a cleared state, so a measurement can
   never be polluted by what this script wrote last time — it cannot feed back
   on itself.

   Without JS the CSS fallback holds each photo at its own 16:9 ratio instead
   of letting it collapse.
   ========================================================================== */
(function () {
  var STACKED = '(max-width: 749px)';
  var MIN_BAND = 200;   /* shortest a band may get, in px */

  /* Both module types expose the same two children under different names */
  var MODULES = [
    { box: '.hero-split', media: '.hero-split__media', content: '.hero-split__content' },
    { box: '.image-with-text', media: '.image-with-text__media', content: '.image-with-text__content' },
    /* Shop by stone in its split layout. The stone row takes the place of the
       photo, so it has to take part in the shared band too — otherwise that one
       module would stack to its own height on a phone and break the run of
       identical modules. Its root also carries .image-with-text, but the entry
       above cannot double-count it: that one looks for an
       .image-with-text__media child, which the stone row is not. */
    { box: '.stone-cats__split', media: '.stone-cats__split-media', content: '.stone-cats__split-panel' }
  ];

  /* A hero switched to "full" mode (theme editor > Hero > Layout) is one
     full-bleed image with the copy laid over it, so it owns its own height via
     aspect-ratio and must stay out of this shared-band maths entirely: its
     overlay panel is exactly as tall as the hero (480px on a phone), and
     folding that in as "the tallest text panel" would stretch every banner
     below it to 480px and roughly double the page. Its own box is fine as-is,
     so skipping it costs nothing. */
  /* Two boxes opt out of the shared band:

     .hero-split--full — a full-bleed hero owns its own height via aspect-ratio
       (4/5 on phones), so it must stay out of the maths entirely.

     .image-with-text--contain — the returns banner. Its crop is fixed by CSS
       (see the phone rules in base.css), so the shared band must not write a
       height into it.

     Any element matching SKIP is left exactly as CSS leaves it. */
  var SKIP = '.hero-split--full, .image-with-text--contain';

  /* The returns banner still has to match its own copy. On a phone its photo
     band is pinned by CSS to 10/7 of the width (~301px tall on a 430px phone)
     with the crop fixed so the tissue in the tall bag stays whole, and the
     TEXT panel is what gets stretched: its min-height is set to the photo
     band's height, so the two stacked rows come out the same size. Only the
     text panel is written to — never the photo box — so the ResizeObserver
     that watches the copy cannot feed back on itself. Above 750px CSS already
     stretches both columns to the same row height. */
  var CONTAIN = '.image-with-text--contain';

  /* Returns banner: text band as tall as its own photo band, phones only. */
  function syncContain(scope) {
    var boxes = (scope || document).querySelectorAll(CONTAIN);
    for (var i = 0; i < boxes.length; i++) {
      var media = boxes[i].querySelector('.image-with-text__media');
      var content = boxes[i].querySelector('.image-with-text__content');
      if (!media || !content) continue;
      content.style.minHeight = '';
      if (!window.matchMedia(STACKED).matches) continue;
      var want = Math.round(media.getBoundingClientRect().height);
      if (want > 0) content.style.minHeight = want + 'px';
    }
  }

  var observer = null;

  function collect(scope) {
    var found = [];
    var root = scope || document;
    for (var m = 0; m < MODULES.length; m++) {
      var boxes = root.querySelectorAll(MODULES[m].box);
      for (var i = 0; i < boxes.length; i++) {
        if (boxes[i].matches(SKIP)) continue;
        var media = boxes[i].querySelector(MODULES[m].media);
        var content = boxes[i].querySelector(MODULES[m].content);
        if (media && content) found.push({ box: boxes[i], media: media, content: content });
      }
    }
    return found;
  }

  /* The band height the design calls for, declared in base.css as
     --stacked-band (231px: the size of the product page's own image-with-text
     block, which is the agreed reference). Keeping the number in CSS means the
     layout stays editable without touching this script. */
  function pinnedBand(box) {
    var raw = window.getComputedStyle(box).getPropertyValue('--stacked-band');
    var value = parseFloat(raw);
    return isNaN(value) ? 0 : value;
  }

  function clear(modules) {
    for (var i = 0; i < modules.length; i++) {
      modules[i].media.style.height = '';
      modules[i].content.style.minHeight = '';
    }
  }

  function syncAll(scope) {
    var modules = collect(scope);
    clear(modules);

    /* Two-column layouts: CSS owns every height, so leave it alone */
    if (!window.matchMedia(STACKED).matches) return;

    /* Measure only after the reset — reading forces it to take effect.

       Only the TEXT panels decide the height. Folding the photos in would let a
       single tall image dictate every module on the page: the sample photos are
       1024x1024, so on a 430px phone one of them alone pinned all five modules
       to a 446px band each. A photo can always crop (object-fit: cover), so it
       never needs to set the size. The floor keeps a banner that carries almost
       no copy from collapsing into a sliver.

       max(pinned, measured) rather than the pinned value alone: if copy is later
       lengthened the band grows with it, so text is never clipped — it just
       stops matching the reference until the copy is trimmed again. */
    var tallest = 0;
    var pinned = 0;
    for (var i = 0; i < modules.length; i++) {
      var text = modules[i].content.getBoundingClientRect().height;
      if (text > tallest) tallest = text;
      var want = pinnedBand(modules[i].box);
      if (want > pinned) pinned = want;
    }
    tallest = Math.round(Math.max(tallest, pinned, MIN_BAND));
    if (tallest <= 0) return;

    /* One size for all five modules, photo band and text band alike */
    for (var j = 0; j < modules.length; j++) {
      modules[j].media.style.height = tallest + 'px';
      modules[j].content.style.minHeight = tallest + 'px';
    }
  }

  var queued = null;
  function schedule() {
    if (queued) window.cancelAnimationFrame(queued);
    queued = window.requestAnimationFrame(function () {
      queued = null;
      syncAll(document);
      syncContain(document);
    });
  }

  /* Watch the copy itself so edits and late-loading fonts re-sync too. Only the
     text panels are observed — never the photo boxes — which keeps this
     loop-free. Observing a node twice is a no-op, so calling this again is
     safe. */
  function observePanels(scope) {
    if (!window.ResizeObserver) return;
    if (!observer) observer = new ResizeObserver(schedule);
    var modules = collect(scope);
    for (var i = 0; i < modules.length; i++) observer.observe(modules[i].content);

    /* The returns banner is skipped by collect(), so it needs its own watch */
    var boxes = (scope || document).querySelectorAll(CONTAIN);
    for (var j = 0; j < boxes.length; j++) {
      var content = boxes[j].querySelector('.image-with-text__content');
      if (content) observer.observe(content);
    }
  }

  function refresh(scope) {
    syncAll(scope);
    syncContain(scope);
    observePanels(scope);
  }

  document.addEventListener('DOMContentLoaded', function () { refresh(document); });

  window.addEventListener('load', schedule);
  window.addEventListener('resize', schedule);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);

  /* The theme editor re-renders a section on every settings change */
  document.addEventListener('shopify:section:load', function (event) { refresh(event.target); });
  document.addEventListener('shopify:section:select', function (event) { refresh(event.target); });

  refresh(document);
})();
